import { GAME_CONFIG } from '../config';
import { Character } from '../objects/Character';

export interface DanceConfig {
  bpm: number;
  maxAngle: number;
  lateralPx?: number;
  depthPx?: number;
  phaseRatio?: number;
}

export class DanceSystem {
  character: Character;
  bpm: number;
  maxAngle: number;
  startTime: number;

  private baseBpm: number;
  private baseMaxAngle: number;

  private lateralPx = 0;
  private depthPx = 0;
  private phaseRatio = 0.5;

  /** scene.time.now timestamp before which lateral motion is forced to 0 (e.g.
   * after a MISS reaction). 0 means no suspension active. */
  private lateralSuspendedUntil = 0;

  constructor(character: Character, config?: Partial<DanceConfig>) {
    this.character = character;
    this.baseBpm = config?.bpm ?? GAME_CONFIG.DANCE_BPM;
    this.baseMaxAngle = config?.maxAngle ?? GAME_CONFIG.DANCE_MAX_ANGLE;
    this.bpm = this.baseBpm;
    this.maxAngle = this.baseMaxAngle;
    if (config?.lateralPx !== undefined) this.lateralPx = config.lateralPx;
    if (config?.depthPx !== undefined) this.depthPx = config.depthPx;
    if (config?.phaseRatio !== undefined) this.phaseRatio = config.phaseRatio;
    this.startTime = performance.now();
  }

  update(time: number) {
    const elapsed = (time - this.startTime) / 1000;
    const phase = elapsed * (this.bpm / 60) * Math.PI;
    const angle = Math.sin(phase) * this.maxAngle;
    this.character.setHeadAngle(angle);

    // Lateral / depth body motion. Skip the offset writes entirely when both
    // amplitudes are 0 so stage 1 stays identical to the previous behavior.
    if (this.lateralPx === 0 && this.depthPx === 0) return;

    const sceneTimeNow = this.character.scene.time.now;
    const lateralActive =
      this.lateralPx !== 0 && sceneTimeNow >= this.lateralSuspendedUntil;
    if (lateralActive) {
      const lateralPhase = elapsed * (this.bpm / 60) * Math.PI * this.phaseRatio;
      this.character.setLateralOffset(Math.sin(lateralPhase) * this.lateralPx);
    } else if (this.lateralPx !== 0) {
      // Suspended — snap to center so the character clearly stops swaying.
      this.character.setLateralOffset(0);
    }

    if (this.depthPx !== 0) {
      const depthPhase = elapsed * (this.bpm / 60) * Math.PI * this.phaseRatio * 0.7;
      this.character.setDepthOffset(Math.cos(depthPhase) * this.depthPx);
    }
  }

  setBPM(bpm: number) {
    this.bpm = bpm;
  }

  setMaxAngle(angle: number) {
    this.maxAngle = angle;
  }

  setLateral(px: number) {
    this.lateralPx = px;
    if (px === 0) this.character.setLateralOffset(0);
  }

  setDepth(px: number) {
    this.depthPx = px;
    if (px === 0) this.character.setDepthOffset(0);
  }

  setPhaseRatio(r: number) {
    this.phaseRatio = r;
  }

  /** Pause lateral sway for `durationMs` — character snaps to center until the
   * window expires. Triggered by MISS so the failure mood is visible. */
  suspendLateral(durationMs: number = 900) {
    this.lateralSuspendedUntil = this.character.scene.time.now + durationMs;
  }

  setHappy() {
    this.bpm = this.baseBpm * GAME_CONFIG.HAPPY_BPM_MULT;
    this.maxAngle = this.baseMaxAngle * GAME_CONFIG.HAPPY_ANGLE_MULT;
  }

  reset() {
    this.bpm = this.baseBpm;
    this.maxAngle = this.baseMaxAngle;
    this.lateralSuspendedUntil = 0;
    this.character.setLateralOffset(0);
    this.character.setDepthOffset(0);
    this.startTime = performance.now();
  }
}
